/**
 * Starter email templates non-technical users can pick from when creating
 * a new template. Each one is a polished, email-client safe HTML body
 * (table layout + inline styles) using {{firstName}} / {{surveyUrl}} variables.
 */

const baseStyles = {
  red: '#1a1a1a',
  dark: '#1a1a1a',
  text: '#374151',
  muted: '#6b7280',
  border: '#e5e7eb',
  bg: '#f6f8fb'
};

const wrap = (innerHtml) => `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${baseStyles.bg};padding:48px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:${baseStyles.text};">
  <tr>
    <td align="center">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid ${baseStyles.border};">
        <tr>
          <td style="background:${baseStyles.red};padding:36px 32px;color:#ffffff;">
            <div style="font-size:20px;font-weight:700;letter-spacing:0.3px;">Black Heart Association</div>
          </td>
        </tr>
        <tr>
          <td style="padding:48px 32px;">
            ${innerHtml}
          </td>
        </tr>
        <tr>
          <td style="padding:32px;background:#fafbfc;border-top:1px solid ${baseStyles.border};color:${baseStyles.muted};font-size:12px;line-height:1.6;">
            Black Heart Association &middot; Free heart-health screenings for our community<br/>
            You received this email because you signed up with the Black Heart Association.
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`.trim();

const button = (label, href) => `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:24px 0;">
  <tr>
    <td style="background:${baseStyles.red};border-radius:8px;">
      <a href="${href}" style="display:inline-block;padding:12px 28px;color:#ffffff;text-decoration:none;font-weight:600;font-size:15px;">${label}</a>
    </td>
  </tr>
</table>`.trim();

const SAMPLE_TEMPLATES = [
  {
    key: 'survey-invitation',
    name: 'Survey Invitation',
    description: 'Friendly invite asking the recipient to complete a survey.',
    category: 'survey',
    subject: 'We\u2019d love your feedback, {{firstName}}',
    html: wrap(`
      <h1 style="margin:0 0 12px;font-size:24px;color:${baseStyles.dark};">Hi {{firstName}},</h1>
      <p style="margin:0 0 16px;font-size:15px;line-height:1.6;">
        Thanks for being part of the Black Heart Association community.
        Your voice helps us bring better heart-health resources to the people who need them most.
      </p>
      <p style="margin:0 0 8px;font-size:15px;line-height:1.6;">
        It only takes a couple of minutes \u2014 tap the button below to get started:
      </p>
      ${button('Take the Survey', '{{surveyUrl}}')}
      <p style="margin:24px 0 0;font-size:14px;line-height:1.6;color:${baseStyles.muted};">
        Thank you,<br/>
        The Black Heart Association Team
      </p>
    `),
    text: 'Hi {{firstName}},\n\nThanks for being part of the Black Heart Association community. Please take a moment to complete our survey:\n\n{{surveyUrl}}\n\nThank you,\nThe Black Heart Association Team'
  },
  {
    key: 'event-announcement',
    name: 'Event Announcement',
    description: 'Announce an upcoming screening event or community event.',
    category: 'announcement',
    subject: 'You\u2019re invited: a free heart screening near you',
    html: wrap(`
      <h1 style="margin:0 0 12px;font-size:24px;color:${baseStyles.dark};">Hi {{firstName}},</h1>
      <p style="margin:0 0 16px;font-size:15px;line-height:1.6;">
        We\u2019re hosting a <strong>free community heart-health screening</strong> and we\u2019d love to see you there.
      </p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#fafbfc;border:1px solid ${baseStyles.border};border-radius:8px;margin:0 0 20px;">
        <tr>
          <td style="padding:16px 20px;">
            <div style="font-size:13px;color:${baseStyles.muted};text-transform:uppercase;letter-spacing:0.5px;">Event details</div>
            <div style="margin-top:6px;font-size:16px;font-weight:600;color:${baseStyles.dark};">Add date, time, and location here</div>
          </td>
        </tr>
      </table>
      <p style="margin:0 0 8px;font-size:15px;line-height:1.6;">
        Tap below to RSVP or learn more:
      </p>
      ${button('RSVP Now', '{{surveyUrl}}')}
      <p style="margin:24px 0 0;font-size:14px;line-height:1.6;color:${baseStyles.muted};">
        See you soon,<br/>
        The Black Heart Association Team
      </p>
    `),
    text: 'Hi {{firstName}},\n\nWe\u2019re hosting a free community heart-health screening and we\u2019d love to see you there.\n\nRSVP: {{surveyUrl}}\n\nSee you soon,\nThe Black Heart Association Team'
  },
  {
    key: 'follow-up-reminder',
    name: 'Friendly Reminder',
    description: 'Soft reminder for recipients who haven\u2019t responded yet.',
    category: 'reminder',
    subject: 'Quick reminder, {{firstName}}',
    html: wrap(`
      <h1 style="margin:0 0 12px;font-size:24px;color:${baseStyles.dark};">Hi {{firstName}},</h1>
      <p style="margin:0 0 16px;font-size:15px;line-height:1.6;">
        Just a friendly nudge \u2014 we noticed you haven\u2019t had a chance to respond yet.
        Your input genuinely helps us shape the work we do for our community.
      </p>
      ${button('Continue Where You Left Off', '{{surveyUrl}}')}
      <p style="margin:24px 0 0;font-size:14px;line-height:1.6;color:${baseStyles.muted};">
        Thank you so much,<br/>
        The Black Heart Association Team
      </p>
    `),
    text: 'Hi {{firstName}},\n\nJust a friendly nudge — we noticed you haven\u2019t had a chance to respond yet.\n\nContinue here: {{surveyUrl}}\n\nThank you,\nThe Black Heart Association Team'
  },
  {
    key: 'thank-you',
    name: 'Thank You',
    description: 'Warm thank-you sent after someone completes a survey or event.',
    category: 'follow-up',
    subject: 'Thank you, {{firstName}}!',
    html: wrap(`
      <h1 style="margin:0 0 12px;font-size:24px;color:${baseStyles.dark};">Thank you, {{firstName}}!</h1>
      <p style="margin:0 0 16px;font-size:15px;line-height:1.6;">
        Your response means a lot to us. We\u2019ll use what you shared to keep improving the
        heart-health resources we bring to our community.
      </p>
      <p style="margin:0 0 16px;font-size:15px;line-height:1.6;">
        If there\u2019s anything else you\u2019d like to share, just reply to this email \u2014 a real human will read it.
      </p>
      <p style="margin:24px 0 0;font-size:14px;line-height:1.6;color:${baseStyles.muted};">
        With gratitude,<br/>
        The Black Heart Association Team
      </p>
    `),
    text: 'Thank you, {{firstName}}!\n\nYour response means a lot to us. If there\u2019s anything else you\u2019d like to share, just reply to this email.\n\nWith gratitude,\nThe Black Heart Association Team'
  }
];

export default SAMPLE_TEMPLATES;
